import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { TextBoxAllModule } from '@syncfusion/ej2-angular-inputs';
import { DialogAllModule, DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { LoginService } from '../../../../src/app/service/login.service';
import { ResetPasswordLinkStateService } from '../../../../src/app/service/reset-password-link-state.service';
import { UserService } from '../../../../src/app/service/user.service';
import { AlertPopupComponent } from '../../../components/alert-popup/alert-popup.component';
import { AuthBrandPanelComponent } from '../../../../src/app/shared/auth-brand-panel/auth-brand-panel.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';


/**
 * Component for changing a user's password either from an authenticated session or through a validated reset link.
 * @component
 * @selector app-change-password
 * @example
 *   <app-change-password></app-change-password>
 * @input None - This component does not declare any Angular @Input properties.
 * @output None - This component does not declare any Angular @Output events.
 * @method ngOnInit - Initializes component state, reads reset token query parameters, and loads user/email context.
 * @method validatePassword - Validates the new password against required complexity rules.
 * @method validateConfirmPassword - Ensures the confirmation password is present and matches the new password.
 * @method onSubmit - Validates form data and submits the password change request to the backend.
 * @method clearForm - Resets all password fields and validation/error state.
 * @method closePopup - Handles popup close actions and navigates the user appropriately.
 * @method onAlertConfirm - Dismisses the success alert and navigates away from the component.
 * @method onAlertClose - Dismisses the alert and navigates away from the component.
 * @lifecycle ngOnInit - Subscribes to route query parameters to determine whether the flow is a reset-link flow or a normal password change flow.
 */
@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [TextBoxAllModule, ButtonModule, CommonModule, FormsModule, DialogAllModule, DialogModule, AlertPopupComponent, TranslateModule, AuthBrandPanelComponent],
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.css', '../../../../src/app/shared/styles/auth-shell.css'],
})
export class ChangePasswordComponent {
  oldPassword: string = '';
  newPassword: string = '';
  confirmPassword: string = '';
  validationErrorMessage: string = '';
  apiErrorMessage: string = '';
  alertData = { alertMessage: '', alertVisible: '' };
  submitted: boolean = false;

  /** Flags to control password field visibility toggles. */
  oldPasskey: boolean = false;
  newPasskey: boolean = false;
  confirmPasskey: boolean = false;
  @ViewChild('changePasswordDialog') changePasswordDialog!: DialogComponent;
  
  userId: number = 0;
  userEmail: string = '';
  resetToken: string = '';
  isLoading: boolean = false;

  constructor(
    private router: Router,
    private user: UserService,
    private loginService: LoginService,
    private route: ActivatedRoute,
    private resetPasswordLinkState: ResetPasswordLinkStateService,
    private translate: TranslateService
  ) { }
  /**
   * Initializes the change-password view by reading the reset token from query parameters and restoring user/reset state as needed.
   * @example
   * ngOnInit()
   * undefined
   * @returns {void} No return value.
   */
  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.resetToken = params['data'] || '';

      if (!this.resetToken) {
        this.resetPasswordLinkState.clearState();
        this.userId = this.user.getUserId();
        this.userEmail = sessionStorage.getItem('LoginEmail') || '';
        return;
      }

      const resetLinkState = this.resetPasswordLinkState.getState();

      if (!resetLinkState) {
        this.router.navigate(['/linkedExpired'], { queryParams: { data: this.resetToken } });
        return;
      }

      this.userId = resetLinkState.userId;
      this.userEmail = resetLinkState.userEmail;
      this.resetToken = resetLinkState.resetToken;
    });
  }
 
/**
 * Validates the new password against required security rules and returns an error message if invalid.
 * @example
 * validatePassword()
 * ''
 * @returns {string} Returns an empty string when the password is valid, otherwise returns a validation error message.
 **/
validatePassword(): string {
  if (!this.newPassword) {
    return this.translate.instant('compliance.change_password.password_required');
  }

  if (this.newPassword.length < 8 || this.newPassword.length > 15) {
    return this.translate.instant('compliance.change_password.password_length');
  }

  if (this.newPassword.includes(' ')) {
    return this.translate.instant('compliance.change_password.password_no_spaces');
  }

  if (!/\d/.test(this.newPassword)) {
    return this.translate.instant('compliance.change_password.password_numeric_required');
  }

  if (!/[!@#$%^&*(),.?':{}|<>]/.test(this.newPassword)) {
    return this.translate.instant('compliance.change_password.password_special_char_required');
  }

  return '';
}

validateConfirmPassword(): string {
  if (!this.confirmPassword) {
    return this.translate.instant('compliance.change_password.confirm_password_required');
  }

  if (this.newPassword !== this.confirmPassword) {
    return this.translate.instant('compliance.change_password.passwords_do_not_match');
  }

  return '';
}
/**
 * Submits the change-password form, validates passwords, and calls the password change API.
 * @example
 * onSubmit(form)
 * undefined
 * @param {any} form - The submitted form instance.
 * @returns {void} No return value.
 */
onSubmit(form: any) {
  this.submitted = true;
  this.validationErrorMessage = '';
  this.apiErrorMessage = '';

  // Validate password
  const passwordError = this.validatePassword();
  if (passwordError) {
    this.validationErrorMessage = passwordError;
    return;
  }

  // Validate confirm password
  const confirmPasswordError = this.validateConfirmPassword();
  if (confirmPasswordError) {
    this.validationErrorMessage = confirmPasswordError;
    return;
  }
  

  const params: any = {
    user_id: this.userId || this.user.getUserId(),
    email: this.userEmail || sessionStorage.getItem('LoginEmail') || '',
    new_password: this.newPassword,
    confirm_password: this.confirmPassword,
  };
  
  this.isLoading = true;

  // Skip token validation when the reset link was already validated by the route guard.
  const skipTokenValidation = !!this.resetToken;

  this.loginService.changePassword(params, skipTokenValidation, this.resetToken).subscribe(
    (response) => {
      this.isLoading = false;
      if (response.status === 200) {
         if (this.resetToken) {
              this.resetPasswordLinkState.clearState(); 
          }
      this.alertData = {
         alertMessage: this.translate.instant('compliance.change_password.password_changed_successfully'),
         alertVisible: 'success'
        };
  return;
      } else {
        this.apiErrorMessage = response.body?.error || this.translate.instant('compliance.change_password.something_went_wrong');
      }
    },
    (error) => {
      this.isLoading = false;
      this.apiErrorMessage =
        error.error?.error ||
        error.error?.message ||
        this.translate.instant('compliance.change_password.failed_to_change_password');
    },
     () => {
      this.isLoading = false;
    }
  );
}

  clearForm() {
    this.oldPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.validationErrorMessage = '';
    this.apiErrorMessage = '';
    this.submitted = false;
  }

  private handleExitNavigation(): void {
    if (this.resetToken) {
      this.resetPasswordLinkState.clearState();
      this.clearForm();
      this.router.navigate(['/login']);
      return;
    }

    this.router.navigate(['/dashboard']);
  }

  closePopup(): void {
    this.handleExitNavigation();
  }

  onAlertConfirm(): void {
    this.alertData.alertVisible = '';
    this.handleExitNavigation();
  }

  onAlertClose(): void {
    this.alertData.alertVisible = '';
    this.handleExitNavigation();
  }

}
